import { useMsal } from "@azure/msal-react";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Constants, PropertyConstants } from "../../../../Models/Constants";
import { IDesignSession } from "../../../../Models/IDesignSession";
import { IMultiSectionUnitConfiguration, IUnitConfiguration } from "../../../../Models/IUnitConfiguration";
import { ApiService } from "../../../../Services/ApiService";
import { deCapitalizeFirstLetter } from "../../../../Services/Global";
import { ConditionalTextFieldInputs } from "../../../Atom/ConditionalTextFields/ConditionalTextFieldInputs";
import { ConditionalTextFieldOutputs } from "../../../Atom/ConditionalTextFields/ConditionalTextFieldOutputs";
import { PheTypePlatePackInputForm } from "./PheTypePlatePackInputForm";
import { IPlatePackConfiguration } from "../../../../Models/IPlatePackConfiguration";
import { SMEDesignGuidelines } from "../../../Atom/SMEDesignGuidelines/SMEDesignGuidelines";
import { checkPlatePackMultiSectionValidity } from "../../../../Utils/PlatePackConfgiurationHelper";
import { MultiSectionFlowchart } from "../../../Atom/MultiSectionVisualization/MultiSectionFlowchart";
import logger from '../../../../Services/LoggingService';
import { 
    Box, 
    Button, 
    Chip, 
    CircularProgress, 
    IconButton,
    Paper, 
    Stack, 
    TextField, 
    Typography, 
    useTheme 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { 
    createProductInfoStyles, 
    createProductChipStyles, 
    createTargetChipStyles 
} from '../../../../theme/formStyles';
import { 
    createFlexStyles, 
    createColumnStyles, 
    createHeaderStyles, 
    createControlStyles, 
    createSpacingStyles, 
    createStatusStyles 
} from '../../../../theme/layoutStyles';

interface DesignSessionReviewFormProps {
  showConditionalFields: boolean;
}

/**
 * DesignSessionReviewForm component to display the design session review form using Material UI
 */
export const DesignSessionReviewForm: React.FC<DesignSessionReviewFormProps> = ({ showConditionalFields }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as any;
    const ctx = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);
    const [activeIndex, setActiveIndex] = useState(1);

    const [multiSectionConfig, setMultiSectionConfig] = useState<IMultiSectionUnitConfiguration[]>([]);
    const [flowChartSelectableProperties, setFlowChartSelectableProperties] = useState<string[]>();
    const activeSection = multiSectionConfig.find((x: IMultiSectionUnitConfiguration) => x.sectionNumber === activeIndex) ?? {} as IMultiSectionUnitConfiguration;
    logger.debug("Active section found", { 
        activeIndex, 
        sectionNumber: activeSection.sectionNumber,
        hasActiveSection: !!activeSection.sectionNumber
    }, {
        component: 'DesignSessionReviewForm',
        method: 'activeSection'
    });
    const [showEmelieSection, setShowEmelieSections] = useState<boolean[]>([]);
    const [unitConfiguration] = useState<IUnitConfiguration>(state.unitConfiguration as IUnitConfiguration);

    const [editMode, setEditMode] = useState<boolean>(false);
    const [designSessions, setDesignSessions] = useState<IDesignSession[]>([]);
    const [designSession] = useState<IDesignSession>(state.designSession as IDesignSession);
    const [formIsValid, setFormIsValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [pheTypesList, setPHETypesList] = useState<{ key: string, text: string }[]>();
    const [platePackConfigs, setPlatePackConfigs] = useState<IPlatePackConfiguration[]>([]);
    const currentSectionNumber = activeIndex - 1;

    // Styles - using centralized utilities where possible
    const flexStyles = createFlexStyles(theme);
    const columnStyles = createColumnStyles(theme);
    const headerStyles = createHeaderStyles(theme);
    const controlStyles = createControlStyles(theme);
    const spacingStyles = createSpacingStyles(theme);
    const statusStyles = createStatusStyles(theme);
    
    const styles = {
        // Main layout styles (using centralized)
        pageContainer: spacingStyles.pageContainer,
        
        // Header section styles (using centralized)
        header: headerStyles.pageHeader,
        headerLeft: flexStyles.flexCenterGap2,
        headerNavigation: flexStyles.flexCenter,
        backButton: {
            height: 'fit-content'
        },
        submitButton: {
            marginLeft: theme.spacing(2)
        },

        // Main content layout
        mainContent: {
            display: 'flex',
            flexDirection: {
                xs: 'column',
                lg: 'row'
            },
            gap: theme.spacing(2),
            width: '100%'
        },

        // Column containers (using centralized)
        leftColumnContainer: {
            ...columnStyles.columnContainer,
            // padding: 0, // Override for this specific case
            // width: { xs: '100%', lg: '20%' },
            // flex: { xs: '1 1 100%', lg: '0 0 20%' },
            // maxWidth: { xs: '100%', lg: '20%' }
        },
        configContainer: columnStyles.configColumn,
        smeContainer: columnStyles.smeColumn,
        // smeContainer: {
        //     ...columnStyles.smeColumn,
        //     // width: { xs: '100%', lg: '30%' },
        //     flex: { xs: '1 1 100%', lg: '0 0 30%' },
        //     maxWidth: { xs: '100%', lg: '30%' }
        // },
        emelieStyling: {
            ...columnStyles.primaryColumn,
            // width: { xs: '100%', lg: '25%' },
            // flex: { xs: '1 1 100%', lg: '0 0 25%' },
            // maxWidth: { xs: '100%', lg: '25%' }
        },
        guidelinesContainer: {
            ...columnStyles.primaryColumn,
            padding: theme.spacing(1),
            border: `2px solid ${theme.palette.primary.main}`,
            // width: { xs: '100%', lg: '25%' },
            // flex: { xs: '1 1 100%', lg: '0 0 25%' },
            // maxWidth: { xs: '100%', lg: '25%' },
            '& > div': {
                margin: 0,
                width: '100%'
            }
        },

        // Product information styles (using centralized form styles)
        productInfo: createProductInfoStyles(theme),
        productInfoOverride: {
            borderRadius: theme.shape.borderRadius,
            borderColor: theme.palette.info.main
        },
        productChip: createProductChipStyles(theme),
        targetChip: createTargetChipStyles(theme),

        // Section navigation styles (using centralized)
        sectionNavigation: headerStyles.sectionNavigation,
        flowchartContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: theme.spacing(1),
            width: '100%',
            overflow: 'auto'
        },

        // Column headers and navigation (using centralized)
        columnHeader: headerStyles.sectionHeader,
        columnTitle: {

            minWidth: '100px',
            marginBottom: theme.spacing(1)
        },

        // Button and control styles (using centralized)
        buttonGroup: controlStyles.buttonGroup,
        optionsGroup: controlStyles.optionsGroup,
        optionsLabel: {
            marginRight: 'auto',
            fontWeight: 500,
            alignSelf: 'center'
        },
        sectionControls: controlStyles.sectionControls,

        // Form section styles
        formSection: {
            marginBottom: theme.spacing(3),
            padding: 0,
            borderRadius: theme.shape.borderRadius
        },
        sectionTitle: {
            fontWeight: 500,
            marginBottom: theme.spacing(1.5),
            color: theme.palette.text.primary
        },
        fieldLabel: {
            fontWeight: 500,
            // marginBottom: theme.spacing(0.5),
            display: 'flex',
            alignItems: 'center'
        },
        requiredMark: {
            color: theme.palette.error.main,
            marginLeft: theme.spacing(0.5)
        },
        inputGroup: {
            marginBottom: theme.spacing(2.5)
        },
        commentField: {
            '& .MuiInputBase-root': {
                minHeight: 100
            }
        },

        // Loading and status styles (using centralized)
        loadingContainer: statusStyles.loadingContainer,
        loadingText: statusStyles.loadingText,
        errorText: statusStyles.errorContainer,

        // Utility styles (using centralized)
        flexCenter: flexStyles.flexCenter,
        flexCenterBetween: flexStyles.flexCenterBetween,
        fullWidth: spacingStyles.fullWidth,
        marginBottom1: spacingStyles.marginBottom1,
        marginBottom2: spacingStyles.marginBottom2,

        // Legacy styles (can be removed if not used elsewhere)
        platePackPatterns: {
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2)
        },
        platePackPattern: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(1),
            cursor: 'pointer',
            '&.selected': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2
            }
        },
        inputGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2)
        }
    };

    // Initial load
    useEffect(() => {
        if (!unitConfiguration || !designSession) {
            return;
        }

        if (designSession.source !== null && 
            designSession.source !== undefined && 
            designSession.source !== '' && 
            designSession.source !== Constants.SOURCE_UNASSIGNED) {
            setEditMode(true);
        }
        
        getUnitConfiguration();
    }, [unitConfiguration, designSession]);

    // Update PHE type when designSessions or pheTypesList change
    useEffect(() => {
        if (designSessions?.length && pheTypesList?.length) {
            getPheTypeFromDictionary(designSession);
        }
    }, [designSessions, pheTypesList, designSession]);

    /**
     * Get the unit configuration sections
     */
    const getUnitConfiguration = async () => {
        try {
            const response = await apiService.getAsync(`settings/unitconfigurations/sections`, unitConfiguration);
            if (!response.ok) {
                throw new Error(`Could not get unit configuration. ${response.status} ${response.statusText}`);
            }

            const sections: IMultiSectionUnitConfiguration[] = await response.json();
            const orderedSections = sections.sort((a, b) => b.sectionNumber! - a.sectionNumber!);
            setMultiSectionConfig(orderedSections);
            
            const selectableProps = orderedSections[0].inputs?.concat(orderedSections[0].outputs ?? []);
            setFlowChartSelectableProperties(selectableProps);

            setPlatePackConfigs(Array.from({ length: sections.length }, () => ({} as IPlatePackConfiguration)));
            
            await getPHETypes(unitConfiguration);
            await getPositionDesignSessions(sections);
        } catch (error) {
            logger.error("Failed to get unit configuration", error as Error, null, {
                component: 'DesignSessionReviewForm',
                method: 'getUnitConfiguration'
            });
            setIsLoading(false);
        }
    };

    /**
     * Get the design sessions for the current position and set the plate pack configurations
     */
    const getPositionDesignSessions = async (sections: IMultiSectionUnitConfiguration[]) => {
        try {
            let currentDesignSession: IDesignSession[] = [];

            if (sections[0].isMultiSection === true) {
                const positionResponse = await apiService.getAsync(`designsessions/positions/sections/${state.designSession.sectionId}`);
                if (!positionResponse.ok) {
                    throw new Error(`Could not get positions. ${positionResponse.status} ${positionResponse.statusText}`);
                }
                
                currentDesignSession = await positionResponse.json();
            } else {
                const positionResponse = await apiService.getAsync(`designsessions/${designSession.id}`);
                if (!positionResponse.ok) {
                    throw new Error(`Could not get design sessions. ${positionResponse.status} ${positionResponse.statusText}`);
                }
                
                const designSessionResponse: IDesignSession = await positionResponse.json();
                currentDesignSession.push(designSessionResponse);
            }

            // Create plate pack configurations
            const currentConfigs = currentDesignSession.map(session => ({
                cold_1_channels_per_pass_type_1: session.cold_1_channels_per_pass_type_1,
                cold_1_channels_per_pass_type_2: session.cold_1_channels_per_pass_type_2,
                cold_1_passes: session.cold_1_passes,
                cold_2_channels_per_pass: session.cold_2_channels_per_pass,
                cold_2_passes: session.cold_2_passes,
                cold_plate_pattern_type_1: session.cold_plate_pattern_type_1,
                cold_plate_pattern_type_2: session.cold_plate_pattern_type_2,
                hot_1_channels_per_pass_type_1: session.hot_1_channels_per_pass_type_1,
                hot_1_channels_per_pass_type_2: session.hot_1_channels_per_pass_type_2,
                hot_1_passes: session.hot_1_passes,
                hot_2_channels_per_pass: session.hot_2_channels_per_pass,
                hot_2_passes: session.hot_2_passes,
                hot_plate_pattern_type_1: session.hot_plate_pattern_type_1,
                hot_plate_pattern_type_2: session.hot_plate_pattern_type_2,
                type: session.hot_1_channels_per_pass_type_2 !== undefined && session.hot_1_channels_per_pass_type_2 !== null
                    ? Constants.VaryingTheta
                    : session.hot_2_channels_per_pass !== undefined && session.hot_2_channels_per_pass !== null
                        ? Constants.VaryingChannelsPasses 
                        : Constants.BasicPass
            }));

            setDesignSessions(currentDesignSession);
            setPlatePackConfigs(currentConfigs);
        } catch (error) {
            logger.error("Failed to get position design sessions", error as Error, {
                sectionId: state.designSession.sectionId,
                designSessionId: designSession.id
            }, {
                component: 'DesignSessionReviewForm',
                method: 'getPositionDesignSessions'
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Update the SME PHE plate type
     */
    const setPhePlateTypeSME = (source: { key: string, text: string }[]) => {
        if (!designSessions) return;
        
        const targetSME = source.find(c => c.key === designSession.phePlateTypeSME);
        if (targetSME !== undefined) {
            designSession.phePlateTypeSME = source.find(c => c.key === designSession.phePlateTypeSME)?.text;
        }
    };

    /**
     * Get available PHE types
     */
    const getPHETypes = async (unitInput: IUnitConfiguration) => {
        try {
            const response = await apiService.getAsync(`settings/phetypes/available`, unitInput);
            if (!response.ok) {
                throw new Error(`Could not get PHE types. ${response.status} ${response.statusText}`);
            }
            
            const types: any[] = await response.json();
            types.sort((a, b) => a.text.localeCompare(b.text));
            
            const pheTypes = types.map(phe => ({
                key: phe.key,
                text: phe.text
            }));
            
            setPHETypesList(pheTypes);
            setPhePlateTypeSME(pheTypes);
        } catch (error) {
            logger.error("Failed to get PHE types", error as Error, null, {
                component: 'DesignSessionReviewForm',
                method: 'getPHETypes'
            });
        }
    };

    /**
     * Get the PHE type from dictionary
     */
    const getPheTypeFromDictionary = (designSession: IDesignSession) => {
        const target = pheTypesList?.find(c => c.key === designSession.phePlateType);
        if (target !== undefined) {
            designSession.phePlateType = pheTypesList?.find(c => c.key === designSession.phePlateType)?.text;
        }

        return designSession.phePlateType;
    };

    /**
     * Handle input changes
     */
    const handleChange = (property: string, value: any, isSME?: boolean) => {
        let current = designSessions[currentSectionNumber];
        if (isSME) {
            property = property + "SME";
        }
        
        if (value?.currentTarget !== undefined) {
            value = value.currentTarget.value;
        }

        // Create a copy of the sessions array
        const currentSessions = [...designSessions];
        
        // If changing PHE plate type, apply to all sections
        if (property === PropertyConstants.PHEPlateTypeSME) {
            currentSessions.forEach(session => {
                // Use type assertion to handle dynamic property access
                (session as any)[deCapitalizeFirstLetter(property)] = value;
            });
        } else {
            // Update just the current section
            current = { 
                ...current, 
                [deCapitalizeFirstLetter(property)]: value 
            };
            currentSessions[currentSectionNumber] = current;
        }

        setDesignSessions(currentSessions);
        validateForm();
    };

    /**
     * Submit form to API
     */
    const submitForm = async () => {
        setIsLoading(true);

        try {
            for (let i = 0; i < multiSectionConfig.length; i++) {
                const body = { 
                    ...designSessions[i], 
                    ...platePackConfigs[i] 
                };
                
                const response = await apiService.putAsync(
                    body, 
                    `designsessions/positions/${designSessions[i].id}`
                );
                
                if (!response.ok) {
                    throw Error(`Could not send response. ${response.status} ${response.statusText}`);
                }
            }
            
            navigate(-1);
        } catch (error) {
            logger.error("Failed to submit design session form", error as Error, {
                sectionsCount: multiSectionConfig.length,
                designSessionIds: designSessions.map(ds => ds.id)
            }, {
                component: 'DesignSessionReviewForm',
                method: 'submitForm'
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Set SME design guideline ID for all sections
     */
    const getSMEGiudelineId = (id?: bigint) => {

        
        // Set same guideline ID for all sections
        const currentSessions = [...designSessions];
        currentSessions.forEach(session => {
            session['smeDesignGuideLineId'] = id;
        });
        
        setDesignSessions(currentSessions);
    };

    /**
     * Validate form inputs
     */
    const validateForm = (input?: boolean) => {
        if ((input === true || input === undefined) &&
            checkMultiSectionValidity() === true &&
            (document.forms[0] === undefined || document.forms[0]?.checkValidity() === true)) {
            setFormIsValid(true);
        } else {
            setFormIsValid(false);
        }
    };

    /**
     * Check validity of all sections
     */
    const checkMultiSectionValidity = () => {
        // Check that all sections have PHE plate type
        if (designSessions.some(session => 
            session.phePlateTypeSME == null || 
            session.phePlateTypeSME === '')) {
            return false;
        }

        // Check that all sections have a source
        if (designSessions.some(session => 
            session.source == null || 
            session.source === '' || 
            session.source === Constants.SOURCE_UNASSIGNED)) {
            return false;
        }

        // Check plate pack validity
        return checkPlatePackMultiSectionValidity(platePackConfigs);
    };

    /**
     * Handle plate pack configuration change
     */
    const handlePlatePackConfigurationChange = (config: IPlatePackConfiguration) => {
        const currentConfigs = [...platePackConfigs];
        currentConfigs[currentSectionNumber] = config;
        setPlatePackConfigs(currentConfigs);
        validateForm();
    };

    /**
     * Set the source selection for a session
     */
    const setSelectionForSession = (source: string, sectionNumber: number) => {
        const currentSessions = [...designSessions];
        currentSessions[sectionNumber].source = source;
        setDesignSessions(currentSessions);
        validateForm();
    };

    /**
     * Check if current section is valid
     */
    const currentSectionValid = (currentSection: number) => {
        const session = designSessions[currentSection];
        
        // Check PHE plate type
        if (!session?.phePlateTypeSME) {
            return false;
        }

        // Check plate pack validity
        const plateConfigs = [platePackConfigs[currentSection]];
        return checkPlatePackMultiSectionValidity(plateConfigs);
    };

    /**
     * Toggle visibility of EMELIE section
     */
    const setEmelieViewSection = (currentSectionNumber: number) => {
        const currentSessions = [...showEmelieSection];
        currentSessions[currentSectionNumber] = !currentSessions[currentSectionNumber];
        setShowEmelieSections(currentSessions);
    };

    // Render the SME container
    const renderSMEContainer = () => (
        <Paper sx={styles.smeContainer} elevation={1}>
            <Box component="form" onSubmit={(e: any) => { e.preventDefault(); }}>
                <Box sx={styles.columnHeader}>
                    <Typography variant="h3" fontWeight="bold" sx={styles.columnTitle}>SME</Typography>
                    
                    <Box sx={styles.optionsGroup}>
                        <Typography variant="body2" sx={styles.optionsLabel}>
                            Select review option:
                        </Typography>
                        <Button
                            variant={designSessions[currentSectionNumber].source === Constants.SOURCE_SME ? "contained" : "outlined"}
                            color="warning"
                            size="small"
                            onClick={() => setSelectionForSession(Constants.SOURCE_SME, currentSectionNumber)}
                            startIcon={designSessions[currentSectionNumber].source === Constants.SOURCE_SME ? <CheckIcon /> : undefined}
                        >
                            Keep SME
                        </Button>
                    </Box>
                </Box>
                
                {designSessions && (
                    <Box sx={styles.formSection}>
                        <Typography variant="subtitle1" sx={styles.sectionTitle}>
                            Plate pack configuration
                            {multiSectionConfig.length > 1 && ` section ${currentSectionNumber + 1}`}
                        </Typography>
                        
                        <Box sx={styles.inputGroup}>
                            <Box sx={styles.fieldLabel}>
                                PHE Type<Typography component="span" sx={styles.requiredMark}>*</Typography>
                            </Box>
                            <PheTypePlatePackInputForm
                                isReadOnly={false}
                                existingConfig={platePackConfigs[currentSectionNumber]}
                                pheTypes={pheTypesList!}
                                setConfig={(c: IPlatePackConfiguration) => handlePlatePackConfigurationChange(c)}
                                setPheType={(phe: string) => { handleChange(PropertyConstants.PHEPlateTypeSME, phe); }}
                                existingPheType={designSessions[0].phePlateTypeSME ?? ''}
                                shouldValidateFormInputs={true}
                                validateForm={validateForm}
                                label=""
                            />
                        </Box>
                    </Box>
                )}
                
                {designSessions?.length > 0 && designSessions[currentSectionNumber] && (
                    <Box sx={styles.formSection}>
                        <Typography variant="subtitle1" sx={styles.sectionTitle}>Process outputs</Typography>
                        <ConditionalTextFieldOutputs
                            disabled={false}
                            handleChange={(e: any) => handleChange(e.currentTarget.name, e.currentTarget.value, true)}
                            unitConfiguration={activeSection!}
                            hotMediaFlowrate={designSessions[currentSectionNumber].hotMediaFlowrateSME ?? ''}
                            hotMediaTempIn={designSessions[currentSectionNumber].hotMediaTempInSME ?? ''}
                            hotMediaTempOut={designSessions[currentSectionNumber].hotMediaTempOutSME ?? ''}
                            hotMediaPressureDrop={designSessions[currentSectionNumber].hotMediaPressureDropSME ?? ''}
                            coldMediaFlowrate={designSessions[currentSectionNumber].coldMediaFlowrateSME ?? ''}
                            coldMediaTempIn={designSessions[currentSectionNumber].coldMediaTempInSME ?? ''}
                            coldMediaTempOut={designSessions[currentSectionNumber].coldMediaTempOutSME ?? ''}
                            coldMediaPressureDrop={designSessions[currentSectionNumber].coldMediaPressureDropSME ?? ''}
                            coldMediaWallTemp={designSessions[currentSectionNumber].coldMediaWallTempSME ?? ''}
                            hotMediaWallTemp={designSessions[currentSectionNumber].hotMediaWallTempSME ?? ''}
                            addStyling={true}
                        />
                    </Box>
                )}
                
                <Box sx={styles.formSection}>
                    <Typography variant="subtitle1" sx={styles.sectionTitle}>Comment</Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={designSessions[currentSectionNumber]?.comment ?? ''}
                        onChange={(e: any) => handleChange(PropertyConstants.Comment, e)}
                        sx={styles.commentField}
                        placeholder="Add your comments here..."
                    />
                </Box>
            </Box>
        </Paper>
    );

    // Render the EMELIE container
    const renderEmelieContainer = () => (
        <Paper sx={styles.emelieStyling} elevation={1}>
            <Box sx={styles.columnHeader}>
                <Typography variant="h3" fontWeight="bold" sx={styles.columnTitle}>EMELIE</Typography>
                
                <Box sx={styles.optionsGroup}>
                    
                    <Typography variant="body2" sx={styles.optionsLabel}>
                        Select review option:
                    </Typography>
                    <Box sx={styles.buttonGroup}>
                    <Button
                        variant={designSessions[currentSectionNumber].source === Constants.SOURCE_EMELIE_ACCEPTABLE ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        startIcon={designSessions[currentSectionNumber].source === Constants.SOURCE_EMELIE_ACCEPTABLE ? <CheckIcon /> : undefined}
                        onClick={() => setSelectionForSession(Constants.SOURCE_EMELIE_ACCEPTABLE, currentSectionNumber)}
                    >
                        Acceptable
                    </Button>
                    
                    <Button
                        variant={designSessions[currentSectionNumber].source === Constants.SOURCE_EMELIE_BETTER_THAN_SME ? "contained" : "outlined"}
                        color="success"
                        size="small"
                        startIcon={designSessions[currentSectionNumber].source === Constants.SOURCE_EMELIE_BETTER_THAN_SME ? <CheckIcon /> : undefined}
                        onClick={() => setSelectionForSession(Constants.SOURCE_EMELIE_BETTER_THAN_SME, currentSectionNumber)}
                    >
                        Better than SME
                    </Button>
                    </Box>
                </Box>
            </Box>
            
            <Box sx={styles.formSection}>
                <Box sx={styles.inputGroup}>
                    <Typography variant="subtitle2" sx={{...styles.fieldLabel, marginBottom: theme.spacing(0.5)}}>PHE Type</Typography>
                    <TextField
                        fullWidth
                        disabled
                        size="small"
                        value={getPheTypeFromDictionary(designSessions[currentSectionNumber]!)}
                    />
                </Box>
                
                <Box sx={styles.inputGroup}>
                    <Typography variant="subtitle2" sx={{...styles.fieldLabel, marginBottom: '0px'}}>Plate Pack Configuration</Typography>
                    <TextField
                        fullWidth
                        disabled
                        size="small"
                        value={designSessions[currentSectionNumber]!.platePackConfiguration}
                    />
                </Box>
            </Box>
            
            {unitConfiguration && showConditionalFields && (
                <Box sx={styles.formSection}>
                    <Typography variant="subtitle1" sx={styles.sectionTitle}>Process outputs</Typography>
                    <ConditionalTextFieldOutputs
                        disabled={true}
                        handleChange={handleChange}
                        unitConfiguration={multiSectionConfig[currentSectionNumber]!}
                        addStyling={false}
                        hotMediaFlowrate={designSessions[currentSectionNumber].hotMediaFlowrate}
                        hotMediaPressureDrop={designSessions[currentSectionNumber].hotMediaPressureDrop?.toString()}
                        hotMediaTempIn={designSessions[currentSectionNumber].hotMediaTempIn}
                        hotMediaTempOut={designSessions[currentSectionNumber].hotMediaTempOut}
                        coldMediaFlowrate={designSessions[currentSectionNumber].coldMediaFlowrate}
                        coldMediaPressureDrop={designSessions[currentSectionNumber].coldMediaPressureDrop?.toString()}
                        coldMediaTempIn={designSessions[currentSectionNumber].coldMediaTempIn}
                        coldMediaTempOut={designSessions[currentSectionNumber].coldMediaTempOut}
                        hotMediaWallTemp={designSessions[currentSectionNumber].hotMediaWallTemp}
                        coldMediaWallTemp={designSessions[currentSectionNumber].coldMediaWallTemp}
                    />
                </Box>
            )}
        </Paper>
    );

    return (
        <Box sx={styles.pageContainer}>
            <Box sx={styles.header}>
                <Box sx={styles.headerLeft}>
                    <Button 
                        variant="outlined" 
                        startIcon={<ArrowBackIcon />} 
                        sx={styles.backButton}
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </Button>
                    
                    {designSessions?.length > 0 && (
                        <Box sx={styles.flexCenter}>
                            <Typography variant="body1" component="div">
                                {`${designSessions[currentSectionNumber].industry} / ${designSessions[currentSectionNumber].subIndustry} / ${designSessions[currentSectionNumber].application} / ${designSessions[currentSectionNumber].subApplication} / `}
                                <Typography component="span" fontWeight="bold" variant="body1">
                                    {designSessions[currentSectionNumber].alfaLavalPosition}
                                </Typography>
                            </Typography>
                        </Box>
                    )}
                </Box>
                
                <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<CheckIcon />}
                    onClick={submitForm}
                    disabled={isLoading || !formIsValid}
                    sx={styles.submitButton}
                >
                    {multiSectionConfig.length > 1 ? 'Submit all sections' : 'Submit'}
                </Button>
            </Box>
            
            {isLoading && (
                <Box sx={styles.loadingContainer}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" sx={styles.loadingText}>Loading design session...</Typography>
                </Box>
            )}
            
            {designSessions?.length > 0 && (
                <Box sx={styles.mainContent}>
                    {/* Left column - Configuration details */}
                    <Stack spacing={2} sx={styles.leftColumnContainer}>
                        <Box sx={styles.productInfo}>
                            <Box sx={{...styles.flexCenter, ...styles.marginBottom1}}>
                                <Typography variant="body1" component="span">
                                    Product: 
                                </Typography>
                                <Chip 
                                    label={activeSection?.hotMediaType}
                                    variant="outlined"
                                    size="small"
                                    sx={styles.productChip}
                                />
                            </Box>
                            
                            <Typography variant="body1" sx={styles.marginBottom1}>
                                Process stage: {activeSection?.processStage}
                            </Typography>
                            
                            {designSessions[currentSectionNumber] !== undefined && 
                             activeSection?.unitOperationTargetName !== undefined && (
                                <Box sx={styles.flexCenterBetween}>
                                    <Typography variant="body1" component="div">
                                        Unit operation target: {activeSection.unitOperationTargetName} 
                                    </Typography>
                                    
                                    <Chip
                                        label={`${designSessions[currentSectionNumber].unitOperationTargetValue?.toString()} ${activeSection.unitOperationTargetValueUnit || ''}`}
                                        size="small"
                                        sx={styles.targetChip}
                                    />
                                </Box>
                            )}
                        </Box>
                        
                        <Paper sx={styles.configContainer} elevation={0}>
                            {multiSectionConfig?.length > 1 && (
                                <Box sx={styles.marginBottom2}>
                                    <Box sx={styles.sectionNavigation}>
                                        <IconButton 
                                            onClick={() => { 
                                                setActiveIndex(activeIndex === designSessions.length ? designSessions.length : activeIndex + 1);
                                            }}
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                        
                                        <Typography variant="subtitle1" fontWeight="medium">
                                            Section {activeIndex}
                                        </Typography>
                                        
                                        <IconButton 
                                            onClick={() => setActiveIndex(activeIndex === 1 ? 1 : activeIndex - 1)}
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </Box>
                                    
                                    <Box sx={styles.flowchartContainer}>
                                        <MultiSectionFlowchart
                                            selectableProperties={flowChartSelectableProperties ?? []}
                                            sectionCount={multiSectionConfig.length}
                                            unitInputConfiguration={multiSectionConfig[0]}
                                            scaling={1 / multiSectionConfig.length * 1.8} 
                                        />
                                    </Box>
                                </Box>
                            )}
                            
                            {designSessions && (
                                <Box sx={styles.marginBottom2}>
                                    <Typography variant="subtitle1" fontWeight="medium" sx={styles.marginBottom1}>Process inputs</Typography>
                                    <ConditionalTextFieldInputs
                                        disabled={false}
                                        originPage={Constants.DesignSessionReviewForm}
                                        propertySpecs={[]}
                                        unitConfiguration={activeSection}
                                        coldMediaFlowrate={designSessions[currentSectionNumber].coldMediaFlowrate?.toString()}
                                        coldMediaTempIn={designSessions[currentSectionNumber].coldMediaTempIn?.toString()}
                                        coldMediaTempOut={designSessions[currentSectionNumber].coldMediaTempOut?.toString()}
                                        coldMediaPressureDrop={designSessions[currentSectionNumber].coldMediaPressureDrop?.toString()}
                                        hotMediaFlowrate={designSessions[currentSectionNumber].hotMediaFlowrate?.toString()}
                                        hotMediaTempIn={designSessions[currentSectionNumber].hotMediaTempIn?.toString()}
                                        hotMediaTempOut={designSessions[currentSectionNumber].hotMediaTempOut?.toString()}
                                        hotMediaPressureDrop={designSessions[currentSectionNumber].hotMediaPressureDrop?.toString()}
                                        addStyling={true}
                                        handleChange={undefined} 
                                    />
                                </Box>
                            )}

<Box sx={styles.sectionControls}>
                            {!formIsValid && (
                                <Typography variant="caption" color="error" sx={styles.errorText}>
                                    <strong>Fill out the SME form to be able to show EMELIE result.</strong>
                                </Typography>
                            )}

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={showEmelieSection[currentSectionNumber] && currentSectionValid(currentSectionNumber) ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                disabled={!currentSectionValid(currentSectionNumber) || editMode}
                                onClick={() => setEmelieViewSection(currentSectionNumber)}
                                fullWidth
                            >
                                {showEmelieSection[currentSectionNumber] && currentSectionValid(currentSectionNumber) ? 'Hide Emelie result' : 'Show Emelie result'}
                            </Button>
                        </Box>
                        </Paper>
                        
                        
                    </Stack>
                    
                    {/* Middle column - SME form */}
                    {renderSMEContainer()}
                    
                    {/* Right column - EMELIE results or SME design guidelines */}
                    {((unitConfiguration !== undefined && !showEmelieSection[currentSectionNumber]) || editMode) && (
                        <Box sx={styles.guidelinesContainer}>
                            <SMEDesignGuidelines 
                                isReadOnly={false} 
                                setDesignGuidelineId={(e: bigint | undefined) => getSMEGiudelineId(e)} 
                                designSession={designSession} 
                                unitConfiguration={unitConfiguration} 
                            />
                        </Box>
                    )}
                    
                    {(showEmelieSection[currentSectionNumber] || editMode) && renderEmelieContainer()} 
                </Box>
            )}
        </Box>
    );
}; 